import { useState } from 'react'

import Styles from './ProvideReason.module.scss'

import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import { Input } from 'almanac-ui/TextInputField/Input'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useMutation } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { CancellationReason } from '../PaymentForm/PaymentForm'
import dayjs, { Dayjs } from 'dayjs'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

interface Props {
  onCancel: () => void
  onNext: (periodEnd: Dayjs) => void
}

enum Reason {
  LackingIntegrations = 'lacking_integrations',
  BadAiGenerations = 'bad_ai_generations',
  LackingEditingFeatures = 'lacking_editing_features',
  DoesNotFitNeeds = 'does_not_fit_needs',
  BadReliabilityOrPerformace = 'bad_reliability_or_performance',
  NotWorthValue = 'not_worth_value',
  TooComplex = 'too_complex',
  CustomerService = 'customer_service',
  SwitchedServce = 'switched_server',
  Other = 'other',
}

const reasons = [
  "Doesn't have the integrations I need",
  'AI generations not good enough',
  'Not enough editing features',
  'No longer fits my needs',
  'Reliability or performance concerns',
  "The pricing isn't worth the value",
  "I couldn't figure out how to use it",
  'Customer service was unsatisfactory',
  'I found another tool I like better',
  'Other',
]

const reasonPlaceholders = [
  'Which integrations would you like us to add?',
  "Which wizards weren't good enough for you?",
  'Which editing features were we missing?',
  'What would make you stay around?',
  'What specifically concerned you?',
  'What would make the price worth the value for you?',
  'What specifically was difficult to use?',
  null,
  'Which tool are you switching to?',
  'Please add additional reasons',
]

export const ProvideReason = ({ onCancel, onNext }: Props) => {
  const cancelSubscriptionMutation = useMutation(WorkspaceClient.cancelSubscription)
  const workspaceId = useCurrentWorkspaceId()
  const [selectedReasons, setSelectedReasons] = useState<{ reason: Reason; description: string }[]>([])
  const [error, setError] = useState(false)

  const stripeCancellationReasonForReason = (reason: Reason) => {
    switch (reason) {
      case Reason.LackingIntegrations:
      case Reason.LackingEditingFeatures:
        return CancellationReason.MissingFeatures
      case Reason.BadAiGenerations:
      case Reason.BadReliabilityOrPerformace:
        return CancellationReason.LowQuality
      case Reason.DoesNotFitNeeds:
        return CancellationReason.Unused
      case Reason.NotWorthValue:
        return CancellationReason.TooExpensive
      case Reason.TooComplex:
        return CancellationReason.TooComplex
      case Reason.CustomerService:
        return CancellationReason.CustomerService
      case Reason.SwitchedServce:
        return CancellationReason.SwitchedService
      case Reason.Other:
        return CancellationReason.Other

      default:
        return CancellationReason.Other
    }
  }

  const cancelSubscription = () => {
    if (selectedReasons.length === 0) {
      setError(true)
      return
    }

    // We are "massaging" the reasons we offer into the reasons that Stripe will actually accept.
    // Stripe only allows one reason, so we are just using the first.
    // We'll include all of the data in the description.
    const cancellationReason = stripeCancellationReasonForReason(selectedReasons[0].reason)
    const cancellationDescription = selectedReasons.map((sR) => `${sR.reason}: ${sR.description}`).join(' | ')

    cancelSubscriptionMutation.mutate(
      {
        workspaceId,
        cancellationReason,
        cancellationDescription,
      },
      {
        onSuccess: (res) => {
          const periodEnd = dayjs.unix(res.data.subscription.cancel_at)

          onNext(periodEnd)
        },
        onError: () => {
          showToastMessage('Unable to cancel your subscription', 'error')
        },
      },
    )
  }

  const handleCheckboxChange = (isSelected: boolean, reason: Reason) => {
    setError(false)

    if (selectedReasons.map((r) => r.reason).includes(reason)) {
      setSelectedReasons(selectedReasons.filter((r) => r.reason !== reason))

      return
    }

    setSelectedReasons([...selectedReasons, { reason, description: '' }])
  }

  const handleInputChange = (value: string, reason: string) => {
    const newSelectedReasons = selectedReasons.map((selectedReason, i) => {
      if (selectedReason.reason === reason) {
        return {
          ...selectedReason,
          description: value,
        }
      }

      return selectedReason
    })

    setSelectedReasons(newSelectedReasons)
  }

  return (
    <div>
      <div className={Styles.header}>
        <Heading level={2}>Cancel Subscription</Heading>
      </div>
      <div className={Styles.content}>
        <Text variant="primary" color="var(--dark-80)">
          Please tell us why you're cancelling to help us improve Blaze.
        </Text>
        {error ? (
          <Text variant="secondary" color="var(--red-70)">
            Must select at least one option *
          </Text>
        ) : (
          <Text variant="secondary" color="var(--dark-80)">
            Click any / all that apply:
          </Text>
        )}
        <div className={Styles.reasons}>
          {Object.entries(Reason).map(([_key, value], index) => (
            <div className={Styles.reasonContainer} key={value}>
              <Checkbox
                size="md"
                isSelected={selectedReasons.map((selectedReason) => selectedReason.reason).includes(value)}
                onChange={(isSelected) => handleCheckboxChange(isSelected, value)}
                aria-label={value}
              >
                <div className={Styles.reason}>
                  <Text variant="primary" color="var(--dark-80)">
                    {reasons[index]}
                  </Text>
                </div>
              </Checkbox>
              {selectedReasons.map((selectedReason) => selectedReason.reason).includes(value) &&
                reasonPlaceholders[index] && (
                  <div className={Styles.input}>
                    <Input
                      name="reason"
                      onChange={(event) => handleInputChange(event.target.value, value)}
                      placeholder={reasonPlaceholders[index] || undefined}
                    />
                  </div>
                )}
            </div>
          ))}
        </div>
      </div>
      <div className={Styles.footer}>
        <Button size="lg" variant="tertiary" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          size="lg"
          variant="primary"
          onPress={cancelSubscription}
          isDisabled={cancelSubscriptionMutation.isLoading || error}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
